import { DataTableExpandedRows, DataTablePFSEvent } from 'primereact/datatable';
import { useState, useMemo, useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { UserAccessRightsResponseModel } from '../../../models/users/UserAccessRightsResponseModel';
import { UsersAccessRightsListingResponseModel } from '../../../models/users/UsersAccessRightsListingResponseModel';
import { UsersAccessRightsSearchResponseModel } from '../../../models/users/UsersAccessRightsSearchResponseModel';
import { IHttpService, HttpService } from '../../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../../services/notifierService';
import { useUserAccessRightsFilters } from '../filters/hooks/useUserAccessRightsFilters';
import { IFiltersData } from '../filters/models/filtersData';
import { IBaseResponseModel } from '../../../models/common/BaseResponseModel';
import { IPaginationModel } from '../../../models/common/PaginationModel';
import { PaginatorPageState } from 'primereact/paginator';
import { dataTableSortOrderTypeToString } from '../../../shared/utility';

export const useUsersAccessRights = () => {
	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	const [loading, setLoading] = useState<boolean>(false);
	const [shouldRefetch, setShouldRefetch] = useState<boolean>(false);

	const [usersPaginationModel, setUsersPaginationModel] =
		useState<IPaginationModel>({
			first: 0,
			rows: 10,
			currentPage: 1,
			totalRecords: 0
		});

	const [users, setUsers] = useState<UsersAccessRightsListingResponseModel[]>(
		[]
	);

	const {
		control,
		isFiltersReady,
		filtersOptions,
		setValue: setFilterValue,
		clearFilters
	} = useUserAccessRightsFilters();

	const filtersValues = useWatch<IFiltersData>({
		control: control
	});

	const {
		firstName,
		lastName,
		userName,
		divisionId,
		countryIds,
		brandIds,
		sortBy,
		order,
	} = filtersValues;

	const fetchUsers = async () => {
		setLoading(true);

		await httpService
			.postGeneric<
				IBaseResponseModel<UsersAccessRightsListingResponseModel>
			>(
				`users/accessRights`,
				JSON.stringify({...filtersValues, page: usersPaginationModel.currentPage}),
				undefined,
				false
			)
			.then((users) => {
				const mappedUsers = users.models.map((u) => {
					return {
						id: u.id,
						firstname: u.firstname,
						lastname: u.lastname,
						username: u.username,
						rights: {
							models: [],
							total: 0,
							page: 1,
							totalPages: 25,
							first: 0,
						}
					}
				});

				setUsers(mappedUsers);
				setUsersPaginationModel({
					...usersPaginationModel,
					totalRecords: users.total,
				});
			})
			.catch(() => notifierService.error())
			.finally(() => {
				setLoading(false);
			});
	};

	const onPageChange = (event: PaginatorPageState) => {
		setUsersPaginationModel({
			...usersPaginationModel,
			first: event.first,
			currentPage: event.page + 1,
			rows: event.rows
		});
	};

	const onSort = (event: DataTablePFSEvent) => {
		setFilterValue('sortBy', event.sortField);
		setFilterValue(
			'order',
			dataTableSortOrderTypeToString(event.sortOrder)
		);
	};

	const refetch = () => {
		setShouldRefetch(prev => !prev);
	}

	useEffect(() => {
		fetchUsers();
	}, [
		usersPaginationModel.currentPage,
		sortBy,
		order,
		firstName,
		lastName,
		userName,
		divisionId,
		countryIds,
		brandIds,
		shouldRefetch,
	]);

	return {
		control,
		users,
		setUsers,
		usersPaginationModel,
		loading: loading && !isFiltersReady,
		order,
		sortBy,
		filtersOptions,
		onPageChange,
		onSort,
		refetch,
		clearFilters
	};
};
